import React, { useState, useEffect } from 'react';
import { CreditCard, Package, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { paymentsApi } from '../../lib/api';
import { formatDateTime, formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const PaymentsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await paymentsApi.getHistory();
      setTransactions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
      case 'initiated':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
      case 'complete':
        return 'Payé';
      case 'pending':
      case 'initiated':
        return 'En attente';
      default:
        return 'Échoué';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Historique des paiements
        </h1>
        <p className="text-slate-600 mt-1">Consultez toutes vos transactions</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Aucune transaction</p>
              <p className="text-slate-400 text-sm mt-2">Vos paiements apparaîtront ici</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`transaction-${tx.id}`}>
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Paiement livraison</p>
                    <p className="text-sm text-slate-500">
                      {formatDateTime(tx.created_at)}
                    </p>
                    <p className="text-xs text-slate-400">
                      ID: {tx.session_id?.slice(0, 20)}...
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {getStatusIcon(tx.payment_status)}
                      <span className="text-sm font-medium text-slate-600">
                        {getStatusLabel(tx.payment_status)}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {formatPrice(tx.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
